import React from "react"
import { withRouter, RouteComponentProps } from "react-router-dom"
import CollapsableCard, { CollapsableCardBody, CollapsableCardHeader } from "../../components/CollapsableCard/CollapsableCard"
import DataGrid from "../../components/DataGrid"
import { Column } from "../../components/DataGrid/types/types"
import { alphanumericCompare } from "../../services/utils/sort-util"
import BellringersModal from "./BellringerModal"
import { BellringerQuestionReview } from "./BellringersQuestionReview"
import { BELLRINGER_SUBJECTS, getBellringerSetNumber, getBellringerTestMetaData, getStudentGridData, WEEKLY_QUESTIONS_PER_SUBJECT } from "../../services/bellringers"

const iconCorrect = "/assets/images/icons/v2/ico-answer-status-correct.svg"
const iconIncorrect = "/assets/images/icons/v2/ico-answer-status-incorrect.svg"
const sortMethod = alphanumericCompare

// OT: Initially started as grade by grade but later Jennifer wanted to use Set (based on a feedback from a teacher)
const columnsForGrades: Column[] = [
    {
        title: "WEEK",
        dataProperty: "week",
        sortable: true,
        sortMethod,
    },
    {
        title: "ENGLISH",
        dataProperty: "english",
        sortable: false,
    },
    {
        title: "MATH",
        dataProperty: "math",
        sortable: false,
    },
     {
        title: "READING",
        dataProperty: "reading",
        sortable: false
    },
    // OT: Currently we won't have Science questions (we may use it so KEEP this)!
    // {
    //     title: "SCIENCE",
    //     dataProperty: "science",
    //     sortable: false
    // },
]
interface Props {
}

interface State {
    modalShowQuestion: boolean
    modalShowMicroTest: boolean
    selectedBellringerId: string
    selectedSubject: string
    gridData?: any
}

type AllProps = Props & RouteComponentProps<any>

export class BellringersStudentGradesView extends React.Component<AllProps, State> {
    static instance: BellringersStudentGradesView
    static latestViewedTestId?: string

    constructor(props) {
        super(props)
        this.state = {
            modalShowQuestion: false,
            modalShowMicroTest: false,
            selectedBellringerId:"",
            selectedSubject: "",
        }
        BellringersStudentGradesView.instance = this
    }

    async componentDidMount() {
        let gridData = await getStudentGridData()
        this.setState({gridData})
    }

    static showBellringerQuestion(bellringerId: string, subjectName: string) {
        let instance = BellringersStudentGradesView.instance
        instance.setState({
            modalShowQuestion: true,
            selectedBellringerId: bellringerId,
            selectedSubject: subjectName,
        })
    }

    static startOrResumeBellringersTest(bellringersTestId) {
        let instance = BellringersStudentGradesView.instance
        let testLink = `/app/bellringers/test/${bellringersTestId}`
        BellringersStudentGradesView.latestViewedTestId = bellringersTestId
        instance.props.history.push(testLink)
    }

    handleQuestionModalClose = () => {
        this.setState({modalShowQuestion: false})
    }

    render() {
        let {gridData} = this.state

        if (!gridData) return null

        let grades = gridData
        let {modalShowQuestion, selectedBellringerId, selectedSubject} = this.state
        return (
            <div className="bellringers-student-grades-view">
                {grades.map((gradeInfo, index) => {
                    let { grade, weeks } = gradeInfo
                    let setNumber = getBellringerSetNumber(grade) // Grade 9 -> Set #1
                    let title = `Set ${setNumber}`
                    let collapsed = true
                    let latestViewedTestId = BellringersStudentGradesView.latestViewedTestId

                    if (latestViewedTestId) {
                        let {grade: latestViewedGrade} = getBellringerTestMetaData(latestViewedTestId)
                        if (latestViewedGrade == grade) {
                            collapsed = false
                        }
                    }

                    return (
                        <CollapsableInfoCardForSets
                            key={`bellringers-collapsable-card-${index}`}
                            grade={grade}
                            name={`bellringers-${index}`}
                            title={title}
                            collapsed={collapsed}
                            setNumber={setNumber}
                            columns={columnsForGrades}
                            content={weeks}
                        />
                    )
                })}

                <BellringersModal
                    show={modalShowQuestion}
                    onClose={this.handleQuestionModalClose}
                    dismissible={true}
                    className={selectedSubject ? `sat-explanation modal bellringers-modal--${selectedSubject}` : ""}>
                    <BellringerQuestionReview bellringerId={selectedBellringerId} onClose={this.handleQuestionModalClose} />
                </BellringersModal>
            </div>
        )
    }
}

function CollapsableInfoCardForSets({ grade, name, title, setNumber, ribbonColor = "darkPurple", headerColor = "#279ed7", collapsed = true, columns, content }) {
    let data = getGradesData(grade, content)

    return (
        <CollapsableCard key={name} initiallyCollapsed={collapsed}>
            <CollapsableCardHeader>
                <div className="lesson-list-card-header">
                    <span className={`ribbon ${ribbonColor}`}>
                        <span className="bellringers-grade-level">{setNumber}</span>
                    </span>
                    <h2 className="lesson-list-card-header--text">{title}</h2>
                </div>
            </CollapsableCardHeader>

            <CollapsableCardBody>
                <DataGrid
                    columns={columns}
                    data={data}
                    className="bellringers-table grades-table"
                />
            </CollapsableCardBody>
        </CollapsableCard>
    )
}

function filterWeeks(weeks: any[]) {
    let filteredWeeks = weeks
        .map((week, weekIndex) => {
            let thisWeekStartButtonsEnabled = isPreviousWeekCompleted(weeks, weekIndex)

            if (!thisWeekStartButtonsEnabled) return null

            return week
        })
        .filter(w => w !== null)

    return filteredWeeks
}

function getGradesData(grade, weeks) {
    let filteredWeeks = filterWeeks(weeks)
    let data = filteredWeeks.map((week, index) => getData(grade, weeks, week, index))

    return data
}


/**
 * Determine if the previos week questions are answered so we can advance to the next!
 * 
 * @param weeks 
 * @param weekIndex 
 * @returns 
 */
function isPreviousWeekCompleted(weeks, weekIndex) {
    if (weekIndex === 0) return true

    let weekData = weeks[weekIndex-1]
    let {english, math, reading} = weekData
    let totalNumberOfQuestions = BELLRINGER_SUBJECTS*WEEKLY_QUESTIONS_PER_SUBJECT // subjects * # of questions per subject
    let answeredQuestions = english.length + math.length + reading.length //+ science.length

    if (answeredQuestions < totalNumberOfQuestions) return false

    return true
}

function getBellringerId(subject, grade, week, question) {
    let bellringerId = `${subject}-${grade}-${week}-${question}`
    return bellringerId
}

function getData(grade, weeks, d, index) {
    let id = `bellringers-grade-data-${index}`
    let { weekIndex, english, math, reading, science } = d

    function getContentforSubject(subjectName, thisWeekStartButtonsEnabled=false) {
        let subject = d[subjectName]
        let nofAnswers = subject.length
        let nofQuestionsPerWeekPerSubject = 3
        let answeredAll = nofAnswers === nofQuestionsPerWeekPerSubject
        let weekNo = weekIndex + 1
        let bellringerTestId = `${subjectName}-${grade}-${weekNo}`

        if (answeredAll) {
            return subject.map(d => {
                let {question, correct} = d
                let imgSrc = correct ? iconCorrect: iconIncorrect
                let imgAlt = correct ? "Correct": "Incorrect"
                let bellringerId = getBellringerId(subjectName, grade, weekNo, question)

                return (
                    <div>
                        <button className={`btn btn-bellringers-question-review ${correct ? "bqr-correct" : "bqr-incorrect"}`} onClick={() => BellringersStudentGradesView.showBellringerQuestion(bellringerId, subjectName)}>
                            Q{question} <img src={imgSrc} alt={imgAlt}/>
                        </button>
                    </div>
                )
            })
        }

        let disabled = !thisWeekStartButtonsEnabled
        if (nofAnswers === 0) {
            return <>{getActionButton({title: "START", action: () => BellringersStudentGradesView.startOrResumeBellringersTest(bellringerTestId), resume: false, disabled})}</>
        }
        else {
            return <>{getActionButton({title: "RESUME", action: () => BellringersStudentGradesView.startOrResumeBellringersTest(bellringerTestId), resume: true, disabled})}</>
        }
    }

    // Can we advance?
    let thisWeekStartButtonsEnabled = isPreviousWeekCompleted(weeks, weekIndex)

    let data = {
        week: {
            content: `Week ${weekIndex+1}`,
            comparator: weekIndex,
        },
        english: {
            content: getContentforSubject("english", thisWeekStartButtonsEnabled),
            comparator: english,
        },
        math: {
            content: getContentforSubject("math", thisWeekStartButtonsEnabled),
            comparator: math,
        },
        reading: {
            content: getContentforSubject("reading", thisWeekStartButtonsEnabled),
            comparator: reading,
        },
        science: {
            content: getContentforSubject("science", thisWeekStartButtonsEnabled),
            comparator: science,
        },
    }

    return {
        id,
        data
    }
}

function getActionButton({title, action, resume, disabled}) {
    let className = `btn btn--bg-${resume ? "green": "blue"} w-100`

    return (
        <button 
            className={className}
            disabled={disabled}
            onClick={action}>
                {title}
        </button>
    )
}

const ConnectedBellringersStudentGradesView = withRouter(BellringersStudentGradesView)

export default (props) => {
    return (
        <ConnectedBellringersStudentGradesView {...props} />
    )
}
